/**
 * Utilities for search query processing
 */

import { isValidMedicationItem } from './prescriptionUtils';

/**
 * Normalizes search query text
 * Removes extra whitespace and trims the query
 */
export const normalizeQuery = (query: string): string => {
  return query.trim().replace(/\s+/g, ' ');
};

/**
 * Gets previous medication data from conversations
 * Returns the most recent conversation's medication data.
 * Only considers conversations that have at least one valid medication item (from WSS).
 * Reuses when current_state is still part of the same draft flow so follow-up text
 * (e.g. quantity after VTT, suggestion selection, pharmacy selection) is sent together
 * with the latest structured meds instead of falling back to an older summary snapshot.
 */
export const getPreviousMedicationData = (conversations: any[]): any[] => {
  const conversationsWithMedData = conversations.filter((conv) => {
    const currentState = String(conv?.currentState ?? "").toLowerCase();
    const canReuseForPayload =
      currentState === "ready_to_confirm" ||
      currentState === "collect_medication" ||
      currentState === "select_suggestion" ||
      currentState === "pharmacy_suggestions";

    return (
      canReuseForPayload &&
      conv?.structuredMedicationData &&
      Array.isArray(conv.structuredMedicationData) &&
      conv.structuredMedicationData.length > 0 &&
      conv.structuredMedicationData.some((item: any) => isValidMedicationItem(item))
    );
  });

  if (conversationsWithMedData.length > 0) {
    const mostRecent = conversationsWithMedData[conversationsWithMedData.length - 1];
    return mostRecent.structuredMedicationData || [];
  }

  return [];
};

/**
 * Returns medication data to send in the outbound payload.
 * If the last conversation with a resolved primary intent had intent "complete", returns [].
 * Otherwise returns previous medication data from conversations.
 */
export const getMedicationDataForPayload = (conversations: any[]): any[] => {
  if (!conversations || conversations.length === 0) {
    return getPreviousMedicationData(conversations);
  }
  const lastWithIntent = [...conversations].reverse().find((c: any) => {
    const resolved =
      (c.primaryIntent != null && c.primaryIntent !== '') ||
      (c.lastIntent != null && c.lastIntent !== '');
    return resolved;
  });
  const effectiveIntent = String(
    lastWithIntent?.primaryIntent ?? lastWithIntent?.lastIntent ?? ''
  ).toLowerCase();
  if (lastWithIntent && effectiveIntent === 'complete') {
    return [];
  }
  return getPreviousMedicationData(conversations);
};

